import center from "@turf/center"
import area from "@turf/area"
import pointOnFeature from "@turf/point-on-feature"
import {
  Geometry,
  Feature,
  Polygon,
  polygon,
  Position,
  MultiPolygon,
} from "@turf/helpers"
import _ from "lodash"

import {
  isValidLineStringCoordinates,
  isValidMultiPolygonCoordinates,
  isValidPolygonCoordinates,
  isValidCoordinate,
} from "./coordinate"

export type Ring = Position[]
export type PolyCoords = Ring[]

// Calculates the center point of a GeoJSON geometry and Return as a Coordinates.
export const getCenterFromGeometry = (geometry?: Geometry): number[] | null => {
  try {
    const { type = null, coordinates = null } = geometry
    if (!type || !coordinates) return null
    const centerPoint = center(geometry)
    return _.get(centerPoint, "geometry.coordinates") // Coordinates
  } catch (error) {
    return null
  }
}

const areaOf = (ring: Ring) => area(polygon([ring]))

/** Pick the largest ring by area from an array of rings. */
export const pickLargestRing = (rings: PolyCoords): Ring => {
  const validRing = rings.filter((r) => Array.isArray(r) && r.length >= 4)
  if (!validRing.length) throw new Error("No valid rings (need ≥ 4 positions).")

  return validRing.reduce((best, ring) =>
    areaOf(ring) > areaOf(best) ? ring : best
  )
}

/**
 * Returns the largest exterior boundary as a Polygon feature.
 * Works with Polygon/MultiPolygon.
 */
export const getExteriorBoundaryPolygon = <T = Polygon | MultiPolygon>(
  polygonal?: Feature<T>
): Feature<Polygon> => {
  const geometry = polygonal?.geometry as Polygon | MultiPolygon
  if (
    !geometry ||
    (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")
  ) {
    throw new Error("Input must be a Polygon or MultiPolygon")
  }

  const largestRing =
    geometry.type === "Polygon"
      ? pickLargestRing(geometry.coordinates)
      : pickLargestRing(
          geometry.coordinates.map((poly) =>
            pickLargestRing(poly)
          ) as unknown as PolyCoords
        )

  return polygon([largestRing])
}

export const getPointOnGeometry = (geometry?: Geometry): number[] | null => {
  try {
    const { type = null, coordinates = null } = geometry
    if (!type || !coordinates) return null
    //TODO:: refactor TypeScript for this centroid's type
    //@ts-ignore
    const centerPoint = pointOnFeature(geometry)
    return _.get(centerPoint, "geometry.coordinates") // Coordinates
  } catch (error) {
    return null
  }
}

export const isValidMultiPolygon = (geometry): boolean => {
  const { type, coordinates } = geometry
  return type === "MultiPolygon" && isValidMultiPolygonCoordinates(coordinates)
}

export const isValidPolygon = (geometry): boolean => {
  const { type, coordinates } = geometry
  return type === "Polygon" && isValidPolygonCoordinates(coordinates)
}

export const isValidLineString = (geometry): boolean => {
  const { type, coordinates } = geometry
  return type === "LineString" && isValidLineStringCoordinates(coordinates)
}

export const isValidPoint = (geometry): boolean => {
  const { type, coordinates } = geometry
  return type === "Point" && isValidCoordinate(coordinates)
}
